import { useEffect, useReducer, useState } from "react";
import type { SlotSignageContent } from "@workspace/api-client-react";
import {
  WEB_LOAD_TIMEOUT_MS,
  WEB_RETRY_MS,
  iframeKey,
  initialWebTileState,
  nextWebTileState,
  refreshDelayMs,
} from "@/player/lib/signage-web";

/** Neutral slate shown when a web tile fails and no fallback image is set. */
function FallbackSlate({ title }: { title: string }) {
  return (
    <div
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background"
      data-testid="zone-web-slate"
    >
      <div className="size-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-xl font-semibold">
        {title.slice(0, 1).toUpperCase() || "•"}
      </div>
      <div className="text-sm text-muted-foreground">{title}</div>
    </div>
  );
}

/**
 * Fallback visual for a dead web tile: the configured image, or the neutral
 * slate. The image itself gets error handling (CB-076) — a broken fallback URL
 * (stale presigned link, deleted asset) is exactly when the fallback matters,
 * so an image load failure degrades to the slate instead of the browser's
 * broken-image glyph. Keyed on the URL so a fresh fallback link retries.
 */
function FallbackVisual({
  fallbackUrl,
  title,
  className,
}: {
  fallbackUrl: string | null | undefined;
  title: string;
  className: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  if (fallbackUrl && !imgFailed) {
    return (
      <img
        key={fallbackUrl}
        src={fallbackUrl}
        alt={title}
        className={className}
        onError={() => setImgFailed(true)}
        data-testid="zone-web-fallback"
      />
    );
  }
  return <FallbackSlate title={title} />;
}

/**
 * Fullscreen web tile for the signage (video channel) surface: a sandboxed
 * iframe with a periodic hard refresh (key bump), a load timeout, and a
 * fallback image / branded slate when the page will not load. Hard-cut in and
 * out — no crossfade for live pages.
 */
export function ZoneWeb({
  content,
  className,
}: {
  content: SlotSignageContent;
  /** Positioning classes from the shell (absolute inset-0 …). */
  className: string;
}) {
  const [state, dispatch] = useReducer(nextWebTileState, initialWebTileState);
  const { status, refreshCount } = state;
  const url = content.url ?? null;

  // New tile (or edited URL): start over.
  useEffect(() => {
    dispatch("reset");
  }, [content.id, url]);

  // Load timeout: a page that never fires onload falls back.
  useEffect(() => {
    if (status !== "loading") return;
    const t = setTimeout(() => dispatch("timeout"), WEB_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [status, refreshCount]);

  // Periodic refresh while healthy; slower retry while failed.
  useEffect(() => {
    if (status === "loaded") {
      const delay = refreshDelayMs(content.refreshSec);
      if (delay == null) return undefined;
      const t = setTimeout(() => dispatch("refresh"), delay);
      return () => clearTimeout(t);
    }
    if (status === "failed") {
      const t = setTimeout(() => dispatch("refresh"), WEB_RETRY_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, refreshCount, content.refreshSec]);

  if (!url) {
    return (
      <FallbackVisual
        fallbackUrl={content.fallbackUrl}
        title={content.title}
        className={className}
      />
    );
  }

  return (
    <>
      <iframe
        key={iframeKey(content.id, refreshCount)}
        src={url}
        title={content.title}
        // Scripts + same-origin storage so real dashboards work; NO
        // top-navigation and no popups — the page cannot hijack the player.
        sandbox="allow-scripts allow-same-origin allow-forms"
        referrerPolicy="no-referrer"
        className={`${className} border-0 bg-white`}
        onLoad={() => dispatch("load")}
        onError={() => dispatch("error")}
        data-testid="zone-web-frame"
      />
      {status === "failed" && (
        <FallbackVisual
          fallbackUrl={content.fallbackUrl}
          title={content.title}
          className={`${className} z-10`}
        />
      )}
    </>
  );
}
